/**
 * アプリケーション全体で使用する定数
 */

/**
 * お知らせカテゴリー名
 * 言語に応じたカテゴリー名を取得する
 */
export const ANNOUNCEMENT_CATEGORY_NAME = {
  ja: 'お知らせ',
  en: 'Announcement',
} as const;

/**
 * お知らせカテゴリー名を取得
 * @param lang 言語コード ('ja' | 'en')
 * @returns カテゴリー名
 */
export const getAnnouncementCategoryName = (lang: 'ja' | 'en'): string => {
  return ANNOUNCEMENT_CATEGORY_NAME[lang];
};

/**
 * SNSリンクのURL
 */
export const TWITTER_URL = 'https://x.com/version0_1';
export const BLUESKY_URL = 'https://bsky.app/profile/v0-1.net';
export const GITHUB_URL = 'https://github.com/version0-1';

/**
 * Buy Me a CoffeeのURL
 */
export const BUY_ME_A_COFFEE_URL = 'https://buymeacoffee.com/version0.1';

/**
 * プライバシーポリシーと利用規約の最終更新日
 */
export const LEGAL_DOCUMENTS_LAST_UPDATED = '2026/01/31';

/**
 * プライバシーポリシーのコンテンツ
 */
export interface PrivacyPolicyContent {
  title: string;
  lastUpdated: string;
  sections: Array<{
    heading: string;
    subheading?: string;
    content: string | string[];
    items?: string[];
  }>;
}

export const PRIVACY_POLICY_CONTENT: Record<'ja' | 'en', PrivacyPolicyContent> = {
  ja: {
    title: 'プライバシーポリシー（第四版）',
    lastUpdated: '最終更新日: 2026年9月25日',
    sections: [
      {
        heading: '1. はじめに',
        content: '当サイト（以下「本サイト」）は、ユーザーの個人情報の保護を重要視しています。本プライバシーポリシーは、本サイトがどのように個人情報を収集、使用、保護するかについて説明します。',
      },
      {
        heading: '2. 収集する情報',
        subheading: '2.1 自動的に収集される情報',
        content: '本サイトでは、以下の情報を自動的に収集する場合があります：',
        items: [
          'IPアドレス',
          'ブラウザの種類とバージョン',
          'OSの種類',
          'アクセス日時',
          '参照元URL',
        ],
      },
      {
        heading: '',
        subheading: '2.2 Cookieおよびブラウザストレージの使用',
        content: [
          '本サイトでは、アクセス解析ツールではCookieを使用していません。',
          '本サイトでは、ユーザー体験向上のため、ブラウザのローカルストレージ（localStorage）を使用して以下の設定を保存しています：',
        ],
        items: [
          'フォント設定（ユーザーが選択したフォント）',
          '言語案内バナーの非表示設定（一度閉じたあと再表示しない）',
        ],
      },
      {
        heading: '',
        subheading: '',
        content: [
          'これらの設定はお使いのブラウザにのみ保存され、サーバーには送信されません。ブラウザの設定からいつでも削除することができます。',
        ],
      },
      {
        heading: '3. 情報の利用目的',
        content: '収集した情報は、以下の目的で利用します：',
        items: [
          'サイトの運営と改善',
          'ユーザー体験の向上',
          'アクセス解析と統計データの作成',
          '不正アクセスの防止',
        ],
      },
      {
        heading: '4. 広告配信について',
        content: [
          '本サイトでは、第三者配信の広告サービス（Google AdSense等）を利用する場合があります。これらの広告配信事業者は、ユーザーの興味に応じた広告を表示するため、本サイトや他のサイトへのアクセス情報を使用することがあります。',
          'ユーザーは、広告設定でパーソナライズド広告を無効にすることができます。詳細については、<a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google広告設定</a>をご確認ください。',
        ],
      },
      {
        heading: '5. アクセス解析ツールについて',
        content: [
          '本サイトでは、アクセス解析のために自作の分析ツールを使用しています。このツールは、ユーザーの個人を特定できる情報を収集せず、サイトの利用状況を統計的に分析するために使用されます。',
          '分析ツールの開発過程については、本サイトのコンテンツとして公開予定です。',
        ],
        items: [
          'Cookieは使用しません',
          'IPアドレスは匿名化されます（IPv4の場合は最後のオクテット、IPv6の場合は最後の80ビットをゼロにします）',
          '収集した個別のアクセスデータはDynamoDBに30日間保存され、その後自動的に削除されます',
          '端末の機種名（Chromeの高エントロピー情報）は、事前に同意いただいた場合のみ取得します。同意は設定画面からいつでも撤回できます',
          'デバイス種別・OS・ブラウザ・国・記事別の国内訳等の週次集計データ（個人を特定できない統計情報）は、保存期間を設けず保存します',
          'ユーザーエージェント、参照元URL、アクセス日時などの情報を収集しますが、これらは個人を特定するためには使用されません',
        ],
      },
      {
        heading: '6. アフィリエイトリンクについて',
        content: [
          '本サイトでは、アフィリエイトプログラムに参加している場合があります。記事内のリンクから商品やサービスを購入した際に、本サイトに一定の手数料が発生する場合があります。これにより、本サイトの運営を継続することができます。',
          'アフィリエイトリンクは、ユーザーの購入価格に影響を与えません。',
        ],
      },
      {
        heading: '7. 第三者への情報提供',
        content: '本サイトは、以下の場合を除き、ユーザーの個人情報を第三者に提供することはありません：',
        items: [
          'ユーザーの同意がある場合',
          '法令に基づく場合',
          '人の生命、身体または財産の保護のために必要がある場合',
        ],
      },
      {
        heading: '8. 個人情報の保護',
        content: '本サイトは、個人情報の保護に関する法令を遵守し、適切な安全管理措置を講じています。',
      },
      {
        heading: '9. プライバシーポリシーの変更',
        content: '本プライバシーポリシーは、法令の変更やサイト運営方針の変更に伴い、予告なく変更される場合があります。変更後のプライバシーポリシーは、本ページに掲載した時点で効力を生じます。',
      },
      {
        heading: '10. お問い合わせ',
        content: '本プライバシーポリシーに関するお問い合わせは、フッターの連絡先までご連絡ください。',
      },
    ],
  },
  en: {
    title: 'Privacy Policy (Version 4)',
    lastUpdated: 'Last Updated: September 25, 2026',
    sections: [
      {
        heading: '1. Introduction',
        content: 'This website (hereinafter referred to as "this site") places great importance on protecting users\' personal information. This Privacy Policy explains how this site collects, uses, and protects personal information.',
      },
      {
        heading: '2. Information We Collect',
        subheading: '2.1 Automatically Collected Information',
        content: 'This site may automatically collect the following information:',
        items: [
          'IP address',
          'Browser type and version',
          'Operating system type',
          'Access date and time',
          'Referrer URL',
        ],
      },
      {
        heading: '',
        subheading: '2.2 Use of Cookies and Browser Storage',
        content: [
          'This site does not use cookies for analytics tools.',
          'This site uses browser local storage (localStorage) to save the following settings to improve user experience:',
        ],
        items: [
          'Font settings (user-selected font)',
          'Language notice banner dismissed state (do not show again after closing)',
        ],
      },
      {
        heading: '',
        subheading: '',
        content: [
          'These settings are stored only in your browser and are not sent to our servers. You can delete them at any time through your browser settings.',
        ],
      },
      {
        heading: '3. Purpose of Information Use',
        content: 'Collected information is used for the following purposes:',
        items: [
          'Site operation and improvement',
          'Enhancing user experience',
          'Access analysis and statistical data creation',
          'Preventing unauthorized access',
        ],
      },
      {
        heading: '4. Advertising',
        content: [
          'This site may use third-party advertising services (such as Google AdSense). These advertising providers may use access information to this site and other sites to display ads based on user interests.',
          'Users can disable personalized ads in their ad settings. For details, please check <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ad Settings</a>.',
        ],
      },
      {
        heading: '5. Analytics Tools',
        content: [
          'This site uses a custom analytics tool for access analysis. This tool does not collect personally identifiable information and is used to statistically analyze site usage.',
          'The development process of the analytics tool will be published as content on this site.',
        ],
        items: [
          'Cookies are not used',
          'IP addresses are anonymized (the last octet for IPv4, or the last 80 bits for IPv6, are set to zero)',
          'Individual access data collected is stored in DynamoDB for 30 days and then automatically deleted',
          'Device model information (Chrome high-entropy Client Hints) is only collected with your prior consent. You can withdraw consent at any time in Settings',
          'Weekly aggregated statistics (device type, OS, browser, country, and per-article country breakdown, etc., containing no personally identifiable information) are retained indefinitely',
          'Information such as user agent, referrer URL, and access time is collected, but this information is not used to identify individuals',
        ],
      },
      {
        heading: '6. Affiliate Links',
        content: [
          'This site may participate in affiliate programs. When users purchase products or services through links in articles, this site may receive a commission. This helps us continue operating this site.',
          'Affiliate links do not affect the purchase price for users.',
        ],
      },
      {
        heading: '7. Disclosure to Third Parties',
        content: 'This site will not provide users\' personal information to third parties except in the following cases:',
        items: [
          'When user consent is obtained',
          'When required by law',
          'When necessary to protect life, body, or property',
        ],
      },
      {
        heading: '8. Protection of Personal Information',
        content: 'This site complies with laws regarding the protection of personal information and implements appropriate security measures.',
      },
      {
        heading: '9. Changes to Privacy Policy',
        content: 'This Privacy Policy may be changed without notice due to changes in laws or site operation policies. The revised Privacy Policy will take effect when posted on this page.',
      },
      {
        heading: '10. Contact',
        content: 'For inquiries regarding this Privacy Policy, please contact us through the contact information in the footer.',
      },
    ],
  },
};

/**
 * 利用規約のコンテンツ
 */
export interface TermsOfServiceContent {
  title: string;
  lastUpdated: string;
  sections: Array<{
    heading: string;
    subheading?: string;
    content: string | string[];
    items?: string[];
  }>;
}

export const TERMS_OF_SERVICE_CONTENT: Record<'ja' | 'en', TermsOfServiceContent> = {
  ja: {
    title: '利用規約',
    lastUpdated: '最終更新日: 2026年1月1日',
    sections: [
      {
        heading: '1. はじめに',
        content: '本利用規約（以下「本規約」）は、当サイト（以下「本サイト」）の利用条件を定めるものです。本サイトを利用するすべてのユーザー（以下「ユーザー」）は、本規約に同意したものとみなされます。',
      },
      {
        heading: '2. サイトの利用',
        subheading: '2.1 利用目的',
        content: '本サイトは、情報提供を目的として運営されています。ユーザーは、本サイトのコンテンツを個人的かつ非商業的な目的でのみ利用することができます。',
      },
      {
        heading: '',
        subheading: '2.2 利用制限',
        content: 'ユーザーは、以下の行為を行ってはなりません：',
        items: [
          '本サイトのコンテンツを無断で複製、転載、改変すること',
          '本サイトの運営を妨害する行為',
          '不正アクセスやその他の違法行為',
          '他のユーザーや第三者に不利益を与える行為',
          '公序良俗に反する行為',
        ],
      },
      {
        heading: '3. 知的財産権',
        content: [
          '本サイトに掲載されているすべてのコンテンツ（文章、画像、デザイン等）の著作権は、本サイト運営者または正当な権利者に帰属します。',
          'ユーザーは、本サイトのコンテンツを無断で複製、転載、改変、配布することはできません。ただし、引用の範囲内での利用は、著作権法に基づき認められています。',
        ],
      },
      {
        heading: '4. 免責事項',
        subheading: '4.1 情報の正確性',
        content: '本サイトに掲載されている情報は、可能な限り正確な情報を提供するよう努めていますが、その正確性、完全性、最新性を保証するものではありません。',
      },
      {
        heading: '',
        subheading: '4.2 損害の責任',
        content: '本サイトの利用により生じた損害について、本サイト運営者は一切の責任を負いません。ユーザーは、自己の責任において本サイトを利用するものとします。',
      },
      {
        heading: '',
        subheading: '4.3 サービスの中断・終了',
        content: '本サイトは、予告なくサービスの中断、変更、終了を行う場合があります。これにより生じた損害について、本サイト運営者は責任を負いません。',
      },
      {
        heading: '5. 広告・アフィリエイトリンクについて',
        subheading: '5.1 広告の表示',
        content: '本サイトでは、第三者配信の広告サービス（Google AdSense等）を利用する場合があります。広告の内容は、広告配信事業者が決定するものであり、本サイト運営者は広告の内容について責任を負いません。',
      },
      {
        heading: '',
        subheading: '5.2 アフィリエイトリンク',
        content: [
          '本サイトでは、アフィリエイトプログラムに参加している場合があります。記事内のリンクから商品やサービスを購入した際に、本サイトに一定の手数料が発生する場合があります。',
          'アフィリエイトリンクは、ユーザーの購入価格に影響を与えません。また、アフィリエイトリンクの有無に関わらず、本サイトのコンテンツの品質や信頼性は変わりません。',
        ],
      },
      {
        heading: '6. 外部リンク',
        content: '本サイトには、外部サイトへのリンクが含まれる場合があります。外部サイトの内容や利用規約については、各サイトの運営者が責任を負います。本サイト運営者は、外部サイトの内容について責任を負いません。',
      },
      {
        heading: '7. 規約の変更',
        content: '本規約は、法令の変更やサイト運営方針の変更に伴い、予告なく変更される場合があります。変更後の利用規約は、本ページに掲載した時点で効力を生じます。',
      },
      {
        heading: '8. 準拠法と管轄裁判所',
        content: '本規約は、日本法に準拠して解釈されます。本規約に関する紛争については、本サイト運営者の所在地を管轄する裁判所を専属的合意管轄とします。',
      },
      {
        heading: '9. お問い合わせ',
        content: '本規約に関するお問い合わせは、フッターの連絡先までご連絡ください。',
      },
    ],
  },
  en: {
    title: 'Terms of Service',
    lastUpdated: 'Last Updated: January 1, 2026',
    sections: [
      {
        heading: '1. Introduction',
        content: 'These Terms of Service (hereinafter referred to as "these Terms") set forth the conditions for using this website (hereinafter referred to as "this site"). All users (hereinafter referred to as "Users") who use this site are deemed to have agreed to these Terms.',
      },
      {
        heading: '2. Use of the Site',
        subheading: '2.1 Purpose of Use',
        content: 'This site is operated for the purpose of providing information. Users may use the content of this site only for personal and non-commercial purposes.',
      },
      {
        heading: '',
        subheading: '2.2 Usage Restrictions',
        content: 'Users must not engage in the following acts:',
        items: [
          'Reproducing, reprinting, or modifying the content of this site without permission',
          'Acts that interfere with the operation of this site',
          'Unauthorized access or other illegal acts',
          'Acts that cause disadvantage to other users or third parties',
          'Acts that violate public order and morals',
        ],
      },
      {
        heading: '3. Intellectual Property Rights',
        content: [
          'All copyrights to all content (text, images, designs, etc.) posted on this site belong to the operator of this site or the rightful owner.',
          'Users may not reproduce, reprint, modify, or distribute the content of this site without permission. However, use within the scope of citation is permitted under copyright law.',
        ],
      },
      {
        heading: '4. Disclaimer',
        subheading: '4.1 Accuracy of Information',
        content: 'While we strive to provide accurate information on this site, we do not guarantee its accuracy, completeness, or currency.',
      },
      {
        heading: '',
        subheading: '4.2 Liability for Damages',
        content: 'The operator of this site assumes no responsibility for any damages arising from the use of this site. Users shall use this site at their own risk.',
      },
      {
        heading: '',
        subheading: '4.3 Service Interruption or Termination',
        content: 'This site may interrupt, change, or terminate services without notice. The operator of this site assumes no responsibility for any damages arising from this.',
      },
      {
        heading: '5. Advertising and Affiliate Links',
        subheading: '5.1 Display of Advertisements',
        content: 'This site may use third-party advertising services (such as Google AdSense). The content of advertisements is determined by the advertising provider, and the operator of this site assumes no responsibility for the content of advertisements.',
      },
      {
        heading: '',
        subheading: '5.2 Affiliate Links',
        content: [
          'This site may participate in affiliate programs. When users purchase products or services through links in articles, this site may receive a commission.',
          'Affiliate links do not affect the purchase price for users. Furthermore, the quality and reliability of this site\'s content remain unchanged regardless of the presence of affiliate links.',
        ],
      },
      {
        heading: '6. External Links',
        content: 'This site may contain links to external sites. The operators of each site are responsible for the content and terms of service of external sites. The operator of this site assumes no responsibility for the content of external sites.',
      },
      {
        heading: '7. Changes to Terms',
        content: 'These Terms may be changed without notice due to changes in laws or site operation policies. The revised Terms of Service will take effect when posted on this page.',
      },
      {
        heading: '8. Governing Law and Jurisdiction',
        content: 'These Terms shall be interpreted in accordance with Japanese law. Any disputes regarding these Terms shall be subject to the exclusive jurisdiction of the court having jurisdiction over the location of the operator of this site.',
      },
      {
        heading: '9. Contact',
        content: 'For inquiries regarding these Terms, please contact us through the contact information in the footer.',
      },
    ],
  },
};